import type { ReactElement } from 'react'
import React, { useMemo } from 'react'
import {
  SEVEN_DAYS_AFTER_PURCHASE,
  CANCELLATION_POLICIES,
  CANCELLATION_POLICY_DAYS,
  PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES,
} from 'constants/cancellationPolicies'
import type { Moment } from 'moment'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Colour from 'components/utils/Colour'
import { useIntl, IntlShape } from 'react-intl'

export function getCancellationPolicyText(
  intl: IntlShape,
  cancellationPolicyType: string,
  offerType: App.OfferType,
  refundableUntil?: Moment,
): { highlightText: string | undefined, secondaryText: string | undefined } {
  const freeCancellationText = intl.formatMessage({
    id: 'Q+Vcg0q9UW',
    defaultMessage: 'Free cancellation',
    description: 'Highlight text for free cancellation policy on search tiles',
  })
  const flexibleCancellationText = intl.formatMessage({
    id: 'hCwWYZZ+Qg',
    defaultMessage: 'Flexible cancellation',
    description: 'Highlight text for flexible cancellation policy on search tiles',
  })

  if (offerType == OFFER_TYPE_ALWAYS_ON) {
    if (!refundableUntil) {
      return {
        highlightText: freeCancellationText,
        secondaryText: '',
      }
    }
    return {
      highlightText: freeCancellationText,
      // TODO: Localise this string with intl.formatMessage(). The date format 'DD MMM' should use
      // a locale-aware formatter (e.g. intl.formatDate()) instead of moment's hardcoded English format.
      secondaryText: `until ${refundableUntil.format('DD MMM')}`,
    }
  }
  if (
    cancellationPolicyType === CANCELLATION_POLICIES.POST_PURCHASE_SEVEN_DAYS
  ) {
    return {
      highlightText: freeCancellationText,
      // TODO: Localise this string with intl.formatMessage().
      secondaryText: 'within 7 days of purchase',
    }
  }
  if (PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES.includes(cancellationPolicyType)) {
    if (!refundableUntil) {
      return {
        highlightText: flexibleCancellationText,
        // TODO: Localise this string with intl.formatMessage().
        secondaryText: 'available (hotel only)',
      }
    }
    return {
      highlightText: flexibleCancellationText,
      // TODO: Localise this string with intl.formatMessage(). The date format 'DD MMM' should use
      // a locale-aware formatter (e.g. intl.formatDate()) instead of moment's hardcoded English format.
      secondaryText: `until ${refundableUntil.format('DD MMM')} (hotel only)`,
    }
  }
  return {
    highlightText: undefined,
    secondaryText: undefined,
  }
}

export function getRefundableUntilAndIsRefundableFromCancellationPolicy(
  cancellationPolicyType: App.CancellationPolicyType,
  checkInDate: string | Moment,
  propertyTimezoneOffset: number,
): {
  refundableUntil: Moment | undefined
  isRefundable: boolean
} {
  const days = CANCELLATION_POLICY_DAYS[cancellationPolicyType] || 0

  if (days === 0 || cancellationPolicyType === CANCELLATION_POLICIES.NON_REFUNDABLE) {
    return { refundableUntil: undefined, isRefundable: false }
  }

  let refundableUntil: Moment | undefined

  const checkInMoment = moment(checkInDate)
  if (propertyTimezoneOffset) {
    checkInMoment.utcOffset(propertyTimezoneOffset)
  }

  const startBasedOnCheckIn = checkInDate ? checkInMoment.subtract(days, 'days') : undefined

  const today = moment()
  if (propertyTimezoneOffset) {
    today.utcOffset(propertyTimezoneOffset)
  }

  const startBasedOnToday = today.add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicyType === CANCELLATION_POLICIES.POST_PURCHASE_SEVEN_DAYS) {
    refundableUntil = !startBasedOnCheckIn || startBasedOnToday.isBefore(checkInDate) ?
      startBasedOnToday :
      startBasedOnCheckIn
  } else {
    refundableUntil = startBasedOnCheckIn
  }

  const isRefundable = !refundableUntil || refundableUntil.isAfter(moment())

  return { refundableUntil, isRefundable }
}

interface Props {
  cancellationPolicyType: App.CancellationPolicyType
  checkInDate?: string | Moment
  timezoneOffset?: number
  offerType: App.OfferType
  textSize?: 'small' | 'medium'
  icon?: ReactElement | false
}

function TileCancellationPolicy(props: Props) {
  const {
    cancellationPolicyType,
    checkInDate,
    timezoneOffset,
    offerType,
    textSize = 'medium',
    icon,
  } = props
  const intl = useIntl()

  const { highlightText, secondaryText, isRefundable } = useMemo(() => {
    const { refundableUntil, isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
      cancellationPolicyType,
      checkInDate || '',
      timezoneOffset || 0,
    )
    return {
      ...getCancellationPolicyText(intl, cancellationPolicyType, offerType, refundableUntil),
      isRefundable,
    }
  }, [intl, cancellationPolicyType, checkInDate, timezoneOffset, offerType])

  if (!isRefundable) {
    return null
  }

  return <BodyTextBlock startIcon={icon} variant={textSize}>
    <Colour value="highlight-secondary" as="b">
      {highlightText}&nbsp;
    </Colour>
    {secondaryText}
  </BodyTextBlock>
}

export default TileCancellationPolicy
